import { useCallback, useEffect, useRef, useState } from 'react';

import { SFX_LEVELS } from '../../game/sfx.js';
import Slider from '../components/Slider.jsx';

/**
 * @file THE AUDIO SETTINGS SCREEN — five bus levels and a mute, reached from
 * the pause menu.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT ASKS THE GAMEPLAY SYSTEM, AND IT SURVIVES NOT FINDING ONE
 * ─────────────────────────────────────────────────────────────────────────────
 * The audio is reached exactly the way `PauseMenu` reaches `Input`: walk
 * `engine.systems` for `s.name === 'gameplay'` and take what hangs off it. No
 * engine surface is added for this, and nothing here imports the audio modules —
 * a settings panel that had to `import` the mixer would be a settings panel that
 * cannot be opened in a scene without one.
 *
 * ⚠ IT PROBES FOR THE `setLevel` CAPABILITY, IT DOES NOT NAME A FIELD, and that
 * is not defensive coding — the two fields exist and they are DIFFERENT
 * OBJECTS. `Gameplay` exposes `sys.audio` (`systems/GameAudio.js`, the WIRING:
 * what plays when, `npcShot`/`groanAt`/`carImpact`) and `sys.sfx`
 * (`audio/Sfx.js`, the FACADE: the mixer, the buses and the levels). A volume
 * control belongs to the second, and a reader who guesses from the names alone
 * gets the first — it is the more obvious name and it has no `setLevel` on it.
 * Probing the capability means this file cannot pick the wrong one, and cannot
 * be broken by either object being renamed or by the level API being moved
 * between them.
 *
 * ⚠ **`audio` IS NULL MORE OFTEN THAN IT IS NOT.** Every dev scene, every
 * gallery, every level built before the sound table existed, and hero mode
 * itself (which is silent BY CONSTRUCTION — the table is what gates the whole
 * system) hands this component nothing. So the panel is DISABLED-BUT-PRESENT:
 * the five rows, their labels and their numbers are all still drawn, inert, with
 * one line saying why. A panel that vanished would read as a broken menu, and a
 * panel that threw would take a dev scene's whole HUD down with it. The
 * `VehicleDamage` discipline, applied to pixels.
 *
 * ⚠ THE INERT ROW IS A DIV, NOT A GREYED-OUT `Slider`. `components/Slider.jsx`
 * is deliberately dumb and takes no `disabled` prop, and this file may not grow
 * one onto it; wrapping a live `<input type="range">` in `pointer-events-none`
 * only stops the MOUSE — the control stays in the tab order and the arrow keys
 * still move a value nothing is listening to. The static track is honest.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ARBITRATION AND PERSISTENCE
 * ─────────────────────────────────────────────────────────────────────────────
 * `TimeOfDay.jsx`'s idiom: the CALLER owns the drag, `Slider` stays dumb, and a
 * window-level `mouseup`/`touchend` catches the drag that ends off the element.
 * What the drag buys here is not display arbitration (nothing else writes these
 * levels) but WRITE arbitration — the level is pushed to the mixer on every
 * tick of the drag, because a volume slider that only applies on release is
 * unusable, while `localStorage` is written once on RELEASE. Keyboard changes
 * fire no drag at all, so those persist immediately, and unmount flushes
 * whatever a released-outside-the-window drag left behind.
 *
 * ⚠ `localStorage` THROWS OUTRIGHT in some privacy modes — not "returns null",
 * throws on the property access itself. Both the read and the write are in a
 * try/catch and both failures are silent: a player who cannot persist their
 * volume should still be able to change it.
 *
 * ⚠ The fallback values are `SFX_LEVELS` from `src/game/sfx.js` — the shipped
 * mix, imported rather than retyped. `master` ships at 0.7 on purpose (that
 * file's header explains the headroom), and a second copy of 0.7 in here would
 * be the `links.js`/`countryroads.js` trap: two constants that must move
 * together and no reason for anyone to notice.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 * @param {Function} props.onBack
 */

/** One key, one object. Versioned so a shape change cannot half-load. */
const STORE_KEY = 'harbourside.audio.v1';

/**
 * The rows, in mixing-desk order: the one that scales everything, then the four
 * it scales. `bus` values are the audio contract's own
 * (`'master'|'sfx'|'ambient'|'music'|'ui'`) — the LABEL is the only thing this
 * file gets to choose, which is why 'ambient' prints as "Ambience".
 *
 * ⚠ THIS TABLE IS THE WHOLE FEATURE FOR A NEW BUS. Everything below is driven
 * by it — the rows, the mount-time push, `loadPrefs`, `savePrefs` and
 * `applyStoredAudioPrefs` all iterate `BUSES` and key on `bus` — so Music
 * persists and re-applies with no other edit. ⚠ Note the one thing that does
 * NOT come for free: `SFX_LEVELS` (`src/game/sfx.js`) carries no `music` key, so
 * the fallback at the bottom of the precedence chain is 1, not a shipped
 * default. That is deliberate rather than an omission — the radio's own level is
 * `RADIO_TUNE.gain` 0.55, applied to the TRACK by `GameAudio` and authored
 * beside the stations, and a second number in the mix table would be two places
 * deciding how loud the radio is.
 *
 * ⚠ MUSIC SITS BESIDE AMBIENCE, NOT AT THE END. Both are the CONTINUOUS beds
 * this desk carries and they are the two rows a player balances against each
 * other; UI stays last because it is the only row that is not about the world.
 */
const BUSES = Object.freeze([
  { bus: 'master', label: 'Master', hint: 'Everything' },
  { bus: 'sfx', label: 'SFX', hint: 'Guns, cars, footsteps, doors' },
  { bus: 'ambient', label: 'Ambience', hint: 'Rain, engines, sirens' },
  { bus: 'music', label: 'Music', hint: 'Car radio' },
  { bus: 'ui', label: 'UI', hint: 'Phone, till, notifications' },
]);

/**
 * Finds the object off the gameplay system that owns the BUS LEVELS. Returns
 * null for every scene that has none — see the header; that is the normal case
 * and not an error worth logging.
 *
 * The candidate order is most-specific first, and every one is tested for a
 * callable `setLevel` rather than for merely existing: `sys.audio` is populated
 * on a level with sound and does NOT carry the level API (it is the wiring),
 * so a bare truthiness check on it returns the wrong object and the panel goes
 * live with five sliders wired to nothing.
 *
 * @param {any} engine
 * @returns {any}
 */
function findAudio(engine) {
  const systems = engine?.systems;
  if (!Array.isArray(systems)) return null;
  for (const s of systems) {
    if (s?.name !== 'gameplay') continue;
    for (const c of [s.sfx, s.audio, s.audio?.sfx]) {
      if (c && typeof c.setLevel === 'function') return c;
    }
  }
  return null;
}

/** @returns {{levels: Record<string, number>, muted: boolean}|null} */
function loadPrefs() {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || typeof p !== 'object') return null;
    /** @type {Record<string, number>} */
    const levels = {};
    for (const { bus } of BUSES) {
      const v = Number(p.levels?.[bus]);
      // A stored NaN, a stored string, a bus that did not exist when this was
      // written: fall through to the shipped default rather than to 0. Silence
      // is the one wrong answer here — it reads as a broken game.
      if (Number.isFinite(v)) levels[bus] = Math.min(1, Math.max(0, v));
    }
    return { levels, muted: !!p.muted };
  } catch {
    return null; // privacy mode, disabled storage, corrupt JSON — all the same
  }
}

/** @param {{levels: Record<string, number>, muted: boolean}} prefs */
function savePrefs(prefs) {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(prefs));
  } catch {
    /* nothing we can do, and nothing the player needs told */
  }
}

/**
 * Push the stored preferences into a freshly built level's mixer.
 *
 * ⚠ **THIS EXISTS BECAUSE MOUNTING THE PANEL IS NOT WHEN A SETTING TAKES
 * EFFECT.** The panel applies storage → mixer on mount, which is correct for
 * the panel and useless for everyone else: a player who turned the volume down
 * last session, then started a new one and never opened Settings, got the
 * shipped mix at full level with a stored preference sitting in `localStorage`
 * saying otherwise. A saved setting that only applies once you go and look at
 * it is not a saved setting.
 *
 * ⚠ It reuses `findAudio` deliberately rather than taking the facade directly.
 * "Where do the bus levels live" is a question with one right answer and a
 * tempting wrong one (`sys.audio` is the WIRING and carries no `setLevel`), so
 * it is computed in exactly one place — two probes that can disagree is the
 * `hurtPlayer(amount, lethal)` family.
 *
 * Silent and safe on every path: no storage, no gameplay system, no sound in
 * this level, storage throwing in a privacy mode. Called by `PlayRoute` once
 * the gameplay system is installed.
 *
 * @param {any} engine
 * @returns {boolean} true if something was actually applied — for tests and callers that log.
 */
export function applyStoredAudioPrefs(engine) {
  const audio = findAudio(engine);
  if (!audio) return false;
  const prefs = loadPrefs();
  if (!prefs) return false;
  for (const { bus } of BUSES) {
    const v = prefs.levels[bus];
    if (Number.isFinite(v)) audio.setLevel?.(bus, v);
  }
  audio.setMuted?.(prefs.muted);
  return true;
}

export default function AudioSettings({ engine, onBack }) {
  // Resolved ONCE per mount, through a lazy state initialiser rather than a
  // ref-with-a-null-check: `findAudio` legitimately RETURNS null, so a
  // `current === null` guard would re-walk the system list on every render and
  // never settle. The panel freezes the fixed step, so no system can appear or
  // vanish while it is open.
  const [audio] = useState(() => findAudio(engine));
  const enabled = !!audio;

  const [levels, setLevels] = useState(() => {
    const stored = loadPrefs();
    /** @type {Record<string, number>} */
    const out = {};
    for (const { bus } of BUSES) {
      // Precedence: what the player last chose → what the mixer currently
      // holds → the shipped mix. The middle term is what makes the panel agree
      // with a level some other screen set.
      const live = enabled ? Number(audio.getLevel?.(bus)) : NaN;
      out[bus] = stored?.levels?.[bus] ?? (Number.isFinite(live) ? live : SFX_LEVELS[bus] ?? 1);
    }
    return out;
  });
  const [muted, setMuted] = useState(() => loadPrefs()?.muted ?? (enabled ? !!audio.muted : false));
  const [dragBus, setDragBus] = useState(null);

  // Live state for the flush paths (unmount, window mouseup) — reading React
  // state from inside a listener registered once would flush a stale snapshot.
  const latest = useRef({ levels, muted });
  latest.current = { levels, muted };

  // ── re-apply on mount ─────────────────────────────────────────────────────
  // The stored preference is the player's, and the mixer boots on the shipped
  // mix knowing nothing about it, so somebody has to push it. It is this panel
  // rather than the audio system because the audio system may not import a
  // browser storage API to find out what a player once clicked — but note the
  // cost, and it is the reason a future round should move this to the route:
  // until the settings screen has been OPENED ONCE in a session, the game is
  // playing the shipped defaults.
  useEffect(() => {
    if (!audio) return;
    for (const { bus } of BUSES) audio.setLevel?.(bus, levels[bus]);
    audio.setMuted?.(muted);
    // Mount only: after this, every mutator below pushes its own change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audio]);

  // ── the drag safety net (TimeOfDay's, for the same reason) ────────────────
  // A drag released outside the input never delivers mouseup to it, which would
  // strand `dragBus` and mean the release never persists.
  useEffect(() => {
    if (!dragBus) return undefined;
    const end = () => {
      setDragBus(null);
      savePrefs(latest.current);
    };
    window.addEventListener('mouseup', end);
    window.addEventListener('touchend', end);
    return () => {
      window.removeEventListener('mouseup', end);
      window.removeEventListener('touchend', end);
    };
  }, [dragBus]);

  // Last-chance flush: Esc closes this screen mid-drag often enough to matter.
  useEffect(() => () => savePrefs(latest.current), []);

  const onSlide = useCallback(
    (bus, e) => {
      const v = Math.min(1, Math.max(0, Number(e.target.value)));
      const next = { ...latest.current.levels, [bus]: v };
      setLevels(next);
      // Pushed EVERY tick. A volume control that only lands on release cannot
      // be set by ear, which is the only way anybody sets one.
      audio?.setLevel?.(bus, v);
      // Keyboard (arrow keys) fires no drag, so it has no release to persist on.
      if (!dragBus) savePrefs({ levels: next, muted: latest.current.muted });
    },
    [audio, dragBus],
  );

  const toggleMute = useCallback(() => {
    const next = !latest.current.muted;
    setMuted(next);
    audio?.setMuted?.(next);
    savePrefs({ levels: latest.current.levels, muted: next });
  }, [audio]);

  return (
    <div
      data-hud-overlay=""
      className="hud-fade-in pointer-events-auto absolute inset-0 z-50 flex flex-col bg-void/95 backdrop-blur-[2px]"
    >
      <header className="flex items-baseline justify-between px-6 pt-5 sm:px-10">
        <div>
          <div className="gta-outline font-display text-2xl text-gold">SETTINGS</div>
          <div className="font-hud text-[11px] uppercase tracking-[0.34em] text-hud-faint">
            Audio
          </div>
        </div>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onBack}
          className="rounded-[3px] border border-hairline bg-ink/85 px-4 py-2 font-display text-sm uppercase tracking-wide text-hud transition hover:border-gold hover:text-gold"
        >
          Back
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-8 pt-6 sm:px-10">
        <div className="mx-auto flex max-w-xl flex-col gap-5">
          {/* ⚠ The no-audio notice is a STATEMENT, not a refusal — the same
              shape `docs/phone.md` records for the drone row's status pill. It
              says what is true; it does not remove the controls, because a
              player who cannot see the volume rows cannot tell a silent scene
              from a broken build. */}
          {!enabled ? (
            <p className="rounded-[3px] border border-hairline bg-ink/70 px-4 py-3 font-hud text-[13px] leading-snug text-hud-dim">
              <span className="font-bold uppercase tracking-[0.18em] text-hud-faint">
                No sound in this scene ·{' '}
              </span>
              This level was built without an audio system, so the controls below
              are inert. Your settings are still remembered and will apply the
              next time you play a level that has sound.
            </p>
          ) : null}

          <section className="rounded-[3px] border border-hairline bg-ink/70 px-4 py-4 sm:px-5">
            <h2 className="mb-3 font-hud text-[10px] font-bold uppercase tracking-[0.3em] text-hud-faint">
              Levels
            </h2>
            <div className="flex flex-col gap-4">
              {BUSES.map((b) => (
                <LevelRow
                  key={b.bus}
                  label={b.label}
                  hint={b.hint}
                  value={levels[b.bus]}
                  enabled={enabled}
                  muted={muted}
                  onChange={(e) => onSlide(b.bus, e)}
                  onDragStart={() => setDragBus(b.bus)}
                  onDragEnd={() => {
                    setDragBus(null);
                    savePrefs(latest.current);
                  }}
                />
              ))}
            </div>
          </section>

          {/* MUTE IS ITS OWN BLOCK, not a fifth slider row. It is a different
              kind of control — a latch over the whole desk rather than a level
              on it — and a mute that lives inside the level list is a mute the
              player scrolls past looking for a zero. */}
          <section className="rounded-[3px] border border-hairline bg-ink/70 px-4 py-4 sm:px-5">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="font-display text-[15px] uppercase tracking-wide text-hud">
                  Mute
                </div>
                <p className="mt-0.5 font-hud text-[12px] leading-snug text-hud-dim">
                  Silences every bus without moving a single level.
                </p>
              </div>
              <button
                type="button"
                disabled={!enabled}
                aria-pressed={muted}
                onMouseDown={(e) => e.preventDefault()}
                onClick={enabled ? toggleMute : undefined}
                style={{ touchAction: 'manipulation' }}
                className={`shrink-0 rounded-[3px] border px-4 py-2 font-hud text-[11px] font-bold uppercase tracking-[0.22em] transition ${
                  !enabled
                    ? 'cursor-not-allowed border-hairline/60 bg-ink/40 text-hud-faint/60'
                    : muted
                      ? 'border-siren bg-siren/15 text-siren'
                      : 'border-hairline bg-steel/60 text-hud-dim hover:border-gold hover:text-gold'
                }`}
              >
                {/* ⚠ The button states its CURRENT state, never the action —
                    the `AmmoBar` SAFE rule. A button reading "Unmute" when the
                    sound is on is the ambiguity that makes a player press it
                    twice to find out. */}
                {muted ? 'Muted' : 'Sound on'}
              </button>
            </div>
          </section>
        </div>
      </div>

      <p className="pointer-events-none absolute bottom-5 right-6 select-none font-hud text-[11px] uppercase tracking-[0.3em] text-hud-faint">
        Esc — back
      </p>
    </div>
  );
}

/**
 * ONE level. Label, a percentage, and either a live slider or an inert track.
 *
 * ⚠ THE NUMBER IS A PERCENTAGE AND IT IS ALWAYS PRINTED. A slider pushed to the
 * far left and a broken audio system look identical without it; `0%` is the
 * pixel that tells the player which one they are looking at.
 *
 * While MUTED the row dims and the number is struck through rather than zeroed:
 * the level is still what it is, the mute is just over the top of it, and
 * showing 0% would make un-muting look like it restored a value from nowhere.
 *
 * @param {{label: string, hint: string, value: number, enabled: boolean, muted: boolean,
 *   onChange: Function, onDragStart: Function, onDragEnd: Function}} props
 */
function LevelRow({ label, hint, value, enabled, muted, onChange, onDragStart, onDragEnd }) {
  const pct = Math.round((Number.isFinite(value) ? value : 0) * 100);
  const dim = !enabled || muted;

  return (
    <div className={dim ? 'opacity-50' : ''}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-display text-[14px] uppercase tracking-wide text-hud">{label}</span>
        <span
          className={`shrink-0 font-hud text-[13px] font-bold tabular-nums tracking-[0.1em] ${
            muted && enabled ? 'text-hud-faint line-through' : 'text-gold'
          }`}
        >
          {pct}%
        </span>
      </div>

      {enabled ? (
        <Slider
          className="mt-2"
          min={0}
          max={1}
          step={0.01}
          value={value}
          onChange={onChange}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        />
      ) : (
        // The inert twin. Same 6 px track, same accent, no focus and no keys —
        // see the file header for why this is not a disabled `<input>`.
        <div
          aria-hidden="true"
          className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#1a1a24]"
        >
          <div className="h-full rounded-full bg-[#5a6ff0]" style={{ width: `${pct}%` }} />
        </div>
      )}

      <p className="mt-1 font-hud text-[11.5px] leading-snug text-hud-faint">{hint}</p>
    </div>
  );
}
